import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Select,
  FormErrorMessage,
  VStack,
  Box,
  Text,
  Image,
  useToast,
} from "@chakra-ui/react";
import axiosInstance from "../../helpers/axiosInstance";

export default function AddTrainTicketItemModal({
  isOpen,
  onClose,
  tripId,
  trainTickets,
  setTrainTickets,
  city,
}) {
  const toast = useToast();
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState("");
  const [itemData, setItemData] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setServiceId("");
    setItemData(null);

    (async () => {
      try {
        const res = await axiosInstance.get(
          `/bucket-list/filter-third-party-services?category=transportation&preference=train`
        );
        const raw = res.data.data?.services || [];
        setServices(raw);
      } catch (err) {
        console.error("Error fetching train tickets:", err);
      }
    })();
  }, [isOpen]);

  const validate = () => {
    const e = {};
    if (!serviceId) e.serviceId = "Train ticket selection is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const selected = services.find((s) => s._id === serviceId);
    const payload = {
      service_id: serviceId,
      item_data: selected.data,
      type: "train",
      city,
    };

    try {
      const res = await axiosInstance.post(
        `/bucket-list/${tripId}/add-service-item`,
        payload
      );
      toast({
        title: "Train ticket added",
        description: res.data.message,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      if (res.data.success) {
        // Ensure a unique id for each ticket
        let uniqueId = selected.data.id || selected.data.booking_url;
        if (!uniqueId) {
          uniqueId = `${Date.now()}-${Math.random()}`;
        }
        const newTrainTicket = { ...selected.data, id: uniqueId };
        // Always add the new ticket to the list
        const newList = [...trainTickets, newTrainTicket];

        setTrainTickets(newList);
        setTimeout(onClose, 100); // Give React time to update parent state
      }
    } catch (err) {
      console.error("Error adding train ticket item:", err);
      toast({
        title: "Error",
        description: "Could not add train ticket.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent maxH="80vh" overflowY="auto">
        <ModalHeader>Add Train Ticket from Bucket List</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isInvalid={!!errors.serviceId}>
              <FormLabel>Train Ticket</FormLabel>
              <Select
                placeholder="Select Train Ticket"
                value={serviceId}
                onChange={(e) => {
                  const id = e.target.value;
                  setServiceId(id);
                  const sel = services.find((s) => s._id === id);
                  if (sel) setItemData(sel.data);
                }}
              >
                {services.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.data.train_company} - {s.data.origin_station} ➔ {s.data.destination_station}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.serviceId}</FormErrorMessage>
            </FormControl>

            {itemData && (
              <Box
                borderWidth="1px"
                borderRadius="md"
                p={4}
                mt={2}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box flex="1" pr={4}>
                  <Text fontSize="lg" fontWeight="bold" noOfLines={2} mb={1}>
                    {itemData.train_company}
                  </Text>
                  <Text fontSize="sm" mb={1}>
                    {itemData.origin_station} ➔ {itemData.destination_station}
                  </Text>
                  <Text fontSize="sm" mb={1}>
                    USD {itemData.price?.amount} {itemData.price?.currency}
                  </Text>
                  <Text fontSize="sm">
                    Departure: {itemData.departure_date}
                  </Text>
                  <Text fontSize="sm">
                    Arrival: {itemData.arrival_date}
                  </Text>
                </Box>
                {itemData.image_url && (
                  <Image
                    src={itemData.image_url}
                    boxSize="100px"
                    objectFit="cover"
                    borderRadius="md"
                    alt={itemData.train_company}
                  />
                )}
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} mr={3} variant="ghost">
            Cancel
          </Button>
          <Button colorScheme="customGreen" onClick={handleSave}>
            Add Train Ticket
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
